// ─────────────────────────────────────────────
//  Channel messaging Firestore helpers (Phase 5) — real-time group/broadcast
//  chat. Channels are DERIVED client-side (src/domain/channels.js) and
//  materialized as thin membership-index docs; messages live in one top-level
//  `channelMessages` collection carrying a denormalized `memberIds` copy that
//  the security rules (Plan 02) verify. No Cloud Functions — client + rules.
//
//  Split out of firestore.js to keep that file under the 500-line budget;
//  re-exported from firestore.js so the import surface is unchanged.
// ─────────────────────────────────────────────
import {
  collection, doc, setDoc, addDoc, updateDoc, deleteDoc, getDocs,
  onSnapshot, query, where, serverTimestamp, arrayUnion,
} from "./sdk";
import { db } from "./config";

export const channelsCol        = () => collection(db, "channels");
export const channelMessagesCol = () => collection(db, "channelMessages");

// Materialize a channel doc (Pitfall 5: callers diff-before-write so this only
// runs on a REAL change). Only roster-editors (doctor/AIC/admin) may write per
// the rules. `{ merge: true }` keeps unrelated fields intact.
export const materializeChannel = (channelDoc) =>
  setDoc(
    doc(db, "channels", channelDoc.id),
    {
      facilityId: channelDoc.facilityId ?? null,
      dateKey: channelDoc.dateKey ?? null,
      kind: channelDoc.kind ?? null,
      scopeId: channelDoc.scopeId ?? null,
      label: channelDoc.label ?? "",
      memberIds: channelDoc.memberIds || [],
      postableBy: channelDoc.postableBy ?? "members",
      updatedAt: serverTimestamp(),
    },
    { merge: true }
  );

// Send a channel message. The sender is stamped from the authed uid; memberIds
// is the denormalized membership the rule checks; readBy starts with the sender
// (they've implicitly read their own message — D-11).
export const sendChannelMessage = ({
  channelId, memberIds, facilityId, dateKey, kind, senderId, senderName, body,
}) =>
  addDoc(channelMessagesCol(), {
    channelId,
    memberIds: memberIds || [],
    facilityId: facilityId ?? null,
    dateKey: dateKey ?? null,
    kind: kind ?? null,
    senderId,
    senderName: senderName ?? null,
    body,
    readBy: [senderId],
    sentAt: serverTimestamp(),
  });

// Create a custom group room — an ad-hoc channel with hand-picked members
// (kind "custom"). Auto-id doc; the creator must be in memberIds (rules).
export const createCustomChannel = ({ facilityId, dateKey, label, memberIds, createdBy }) =>
  addDoc(channelsCol(), {
    facilityId: facilityId ?? null,
    dateKey: dateKey ?? null,
    kind: "custom",
    scopeId: "custom",
    label: label || "Group",
    memberIds: memberIds || [],
    postableBy: "members",
    createdBy: createdBy ?? null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

// Add people to an existing custom group (arrayUnion — no read-modify-write).
export const addChannelMembers = (channelId, memberIds) =>
  updateDoc(doc(db, "channels", channelId), {
    memberIds: arrayUnion(...(memberIds || [])),
    updatedAt: serverTimestamp(),
  });

// Live custom groups the user belongs to. Single array-contains (no composite
// index); facility/date/kind are filtered client-side (small volume).
export const listenToMyChannels = (uid, callback) =>
  onSnapshot(
    query(channelsCol(), where("memberIds", "array-contains", uid)),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.error("listenToMyChannels failed:", err)
  );

// Every channel of the day, regardless of membership — the ADMIN oversight
// listener. `listenToMyChannels` filters on `memberIds array-contains uid`,
// which is exactly wrong for oversight: an admin is not a member of other
// people's group rooms, so those rooms would be the one thing full visibility
// still could not see. The rules allow an admin to read any channel doc, so
// this query succeeds for them and permission-denies for everyone else — which
// is why the caller must gate it on isAdmin rather than relying on the error.
export const listenToAllChannelsForDay = (dateKey, callback) =>
  onSnapshot(
    query(channelsCol(), where("dateKey", "==", dateKey)),
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => console.error("listenToAllChannelsForDay failed:", err)
  );

// Delete a custom group room and ALL its messages (the X on a GROUP ROOMS row).
// Rules allow delete only for a custom channel the caller belongs to (+ its
// messages, which the caller is a member of). Best-effort per-doc deletes — a
// partial failure leaves the rest cleaned; the caller swallows errors.
export const deleteCustomChannel = async (channelId) => {
  if (!channelId) return;
  const msgs = await getDocs(
    query(channelMessagesCol(), where("channelId", "==", channelId))
  );
  await Promise.all(msgs.docs.map((d) => deleteDoc(d.ref).catch(() => {})));
  await deleteDoc(doc(db, "channels", channelId)).catch(() => {});
};

// Best-effort midnight purge: delete the user's OWN channel messages whose
// dateKey is NOT today (yesterday's chatter). Scoped to senderId==uid so every
// client cleans only what it authored — collectively the collection is purged,
// and no client can wipe other members' messages (matches the delete rule).
export const deleteStaleChannelMessages = async (uid, todayKey) => {
  if (!uid) return;
  const snap = await getDocs(
    query(channelMessagesCol(), where("senderId", "==", uid))
  );
  await Promise.all(
    snap.docs
      .filter((d) => (d.data().dateKey || null) !== todayKey)
      .map((d) => deleteDoc(d.ref).catch(() => {}))
  );
};

// A member marks a channel message read — arrayUnion is atomic (no read-modify-
// write race) and the update rule (Plan 02) allows ONLY readBy to change (D-11).
export const markChannelMessageRead = (id, uid) =>
  updateDoc(doc(db, "channelMessages", id), { readBy: arrayUnion(uid) });

// One listener per channel. NOTE: no orderBy — combining where(channelId==) +
// orderBy(sentAt) needs a composite index that isn't deployed (same convention
// as listenToAlerts / the DM listeners). We sort by sentAt client-side; per-
// channel daily volume is small (Pitfall 2, convention (a)).
export const listenToChannel = (channelId, callback, onError) =>
  onSnapshot(
    query(channelMessagesCol(), where("channelId", "==", channelId)),
    (snap) => {
      const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      rows.sort(
        (a, b) =>
          (a.sentAt?.toMillis?.() || 0) - (b.sentAt?.toMillis?.() || 0)
      );
      callback(rows);
    },
    (err) => { if (onError) onError(err); }
  );
